import { useCallback } from 'react';
import { IJournal } from 'types';

import {
  usePaginatedJournalsQuery,
  GroupEdge,
  PaginatedJournalsQueryVariables,
  PageInfo,
} from 'core/graphql';
import { isPermissionError } from 'core/utils/isPermissionError';

import { useGroupPermissions } from '../Groups/useGroupPermissions';

type JournalsHookType = {
  data: IJournal[];
  loading?: boolean;
  error?: string;
  /** Отказ по правам (а не сбой) — список говорит «Нет доступа». */
  forbidden: boolean;
  /** Колонка «Преподаватели» — только `GROUP_STAFF_FIELDS_ROLES`. */
  showTeachers: boolean;
  totalCount?: number | null;
  pageInfo?: Pick<
    PageInfo,
    'startCursor' | 'endCursor' | 'hasNextPage' | 'hasPreviousPage'
  >;
  operations: {
    fetchMoreData: (vars: Partial<PaginatedJournalsQueryVariables>) => void;
  };
};

const toIJournalArray = (edge: GroupEdge): IJournal | undefined => {
  const { node } = edge;
  if (node) {
    return {
      id: node.id,
      name: node.name,
      teachersString:
        node.teachers?.map((teacher) => teacher?.fullName || '').join(', ') ||
        '',
      course: node.course.name,
    };
  }
};

const useGetJournalsFromApi = (withStaffFields: boolean) => {
  // `teachers` группы — только `GROUP_STAFF_FIELDS_ROLES`: методисту поле не
  // запрашивается (иначе — отказ поля на весь список журналов).
  const { data, loading, error, fetchMore } = usePaginatedJournalsQuery({
    variables: {
      first: 10,
      withStaffFields,
    },
  });

  const result = (): IJournal[] => {
    const edges = data?.allGroups?.edges;
    if (!edges) {
      return [];
    }

    return edges.reduce((acc: IJournal[], edge) => {
      if (edge) {
        const journals = toIJournalArray(edge as GroupEdge);
        if (journals) {
          return [...acc, journals];
        }
      }

      return acc;
    }, []);
  };

  return {
    fetchMore,
    result: result(),
    loading,
    error,
    pageInfo: data?.allGroups?.pageInfo,
    totalCount: data?.allGroups?.totalCount,
  };
};

export const useJournals = (): JournalsHookType => {
  const { canReadGroupStaffFields } = useGroupPermissions();
  const {
    result,
    loading,
    error,
    totalCount,
    fetchMore,
    pageInfo,
  } = useGetJournalsFromApi(canReadGroupStaffFields);

  const fetchMoreData = useCallback(
    ({ query, after }: Partial<PaginatedJournalsQueryVariables>) => {
      // Ошибка догрузки уже в `error` запроса — обещание гасим, иначе отказ
      // всплывал «Unhandled Rejection» поверх страницы.
      fetchMore({
        variables: {
          query,
          after,
        },
      }).catch(() => undefined);
    },
    [fetchMore],
  );

  return {
    data: result || [],
    loading,
    pageInfo,
    error: error?.message,
    forbidden: isPermissionError(error),
    showTeachers: canReadGroupStaffFields,
    totalCount,
    operations: {
      fetchMoreData,
    },
  };
};
